import { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import { type Customer as StoreCustomer, type Store } from "@/services/features/StoreService";

export const STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY =
  "storeInsights.storePerformanceLive.selection";
export const STORE_INSIGHTS_SHARED_MULTI_SELECTION_SESSION_KEY =
  "storeInsights.multiStoreOverview.selection";
export const STORE_INSIGHTS_MULTI_STORE_OVERVIEW_SELECTION_SESSION_KEY =
  "storeInsights.multiStoreOverview.selection";
export const STORE_INSIGHTS_REPORT_SELECTION_SESSION_KEY =
  "storeInsights.multiStoreOverview.selection";
export const STORE_INSIGHTS_GROUP_COMPARISON_SELECTION_SESSION_KEY =
  "storeInsights.groupComparison.selection";
export const STORE_INSIGHTS_SHARED_DATE_WINDOW_SESSION_KEY = "storeInsights.sharedDateWindow";
export const STORE_INSIGHTS_SHARED_TREND_COMPARISON_SESSION_KEY =
  "storeInsights.sharedTrendComparisonWindow";
export const STORE_INSIGHTS_SHARED_TREND_WEEKDAY_ANCHOR_SESSION_KEY =
  "storeInsights.sharedTrendWeekdayAnchor";
export const STORE_INSIGHTS_SHARED_DEVICE_TYPE_SESSION_KEY = "storeInsights.sharedDeviceType";

export type StoreInsightsTrendComparisonWindow =
  "day_before" | "week_before" | "month_before" | "year_before";

const TREND_COMPARISON_WINDOWS = new Set<StoreInsightsTrendComparisonWindow>([
  "day_before",
  "week_before",
  "month_before",
  "year_before",
]);

export const normalizeTrendComparisonWindow = (
  value: unknown,
): StoreInsightsTrendComparisonWindow | null =>
  typeof value === "string" &&
  TREND_COMPARISON_WINDOWS.has(value as StoreInsightsTrendComparisonWindow)
    ? (value as StoreInsightsTrendComparisonWindow)
    : null;

export interface StoreInsightsMultiSelectionState {
  customerIds: number[];
  groups: string[];
  storeIds: number[];
  deviceType?: DeviceType;
}

export interface StoreInsightsSingleSelectionState {
  storeId: number | null;
  deviceType?: DeviceType;
  /**
   * Distinguishes a store chosen on a single-store page from one seeded from
   * the multi-store scope. This lets each selector retain its own intent.
   */
  hasUserSelectedStore?: boolean;
}

export const isEmptyMultiSelection = (selection: StoreInsightsMultiSelectionState | null) =>
  !selection ||
  (selection.customerIds.length === 0 &&
    selection.groups.length === 0 &&
    selection.storeIds.length === 0);

export type StoreInsightsSharedDateWindowPeriod =
  "yesterday" | "today" | "week_to_date" | "last_weekend" | "last_4_weeks" | "custom";

type LegacyStoreInsightsSharedDateWindowPeriod = "lastWeekend" | "wtd" | "last4w";

const LEGACY_TO_CANONICAL_DATE_WINDOW_PERIOD: Record<
  LegacyStoreInsightsSharedDateWindowPeriod,
  StoreInsightsSharedDateWindowPeriod
> = {
  lastWeekend: "last_weekend",
  wtd: "week_to_date",
  last4w: "last_4_weeks",
};

const ALLOWED_SHARED_DATE_WINDOW_PERIODS = new Set<StoreInsightsSharedDateWindowPeriod>([
  "yesterday",
  "today",
  "week_to_date",
  "last_weekend",
  "last_4_weeks",
  "custom",
]);

export interface StoreInsightsSharedDateWindowState {
  period: StoreInsightsSharedDateWindowPeriod;
  start?: string | null;
  end?: string | null;
}

export const normalizeSharedDateWindowPeriod = (
  value: unknown,
): StoreInsightsSharedDateWindowPeriod | null => {
  if (typeof value !== "string") {
    return null;
  }

  if (ALLOWED_SHARED_DATE_WINDOW_PERIODS.has(value as StoreInsightsSharedDateWindowPeriod)) {
    return value as StoreInsightsSharedDateWindowPeriod;
  }

  return LEGACY_TO_CANONICAL_DATE_WINDOW_PERIOD[value as LegacyStoreInsightsSharedDateWindowPeriod]
    ? LEGACY_TO_CANONICAL_DATE_WINDOW_PERIOD[value as LegacyStoreInsightsSharedDateWindowPeriod]
    : null;
};

const parseOptionalInteger = (value: string | null): number | null => {
  if (!value?.trim()) {
    return null;
  }

  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseIntegerArray = (value: string | null): number[] => {
  if (!value?.trim()) {
    return [];
  }

  return value
    .split(",")
    .map((id) => parseInt(id.trim(), 10))
    .filter((id) => !Number.isNaN(id));
};

const ALLOWED_DEVICE_TYPES = new Set<DeviceType>([
  "all",
  "FITTING_ROOM",
  "IN_AISLE",
  "MOBILE_PICKUP",
  "RFID_CENTRAL",
]);

const parseOptionalDeviceType = (value: unknown): DeviceType | undefined =>
  typeof value === "string" && ALLOWED_DEVICE_TYPES.has(value as DeviceType)
    ? (value as DeviceType)
    : undefined;

export const readSharedDeviceTypeFromSession = (
  sessionKey: string = STORE_INSIGHTS_SHARED_DEVICE_TYPE_SESSION_KEY,
): DeviceType | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return parseOptionalDeviceType(window.sessionStorage.getItem(sessionKey)) ?? null;
  } catch {
    return null;
  }
};

export const writeSharedDeviceTypeToSession = (
  value: DeviceType,
  sessionKey: string = STORE_INSIGHTS_SHARED_DEVICE_TYPE_SESSION_KEY,
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (window.sessionStorage.getItem(sessionKey) !== value) {
      window.sessionStorage.setItem(sessionKey, value);
    }
  } catch {
    // Session storage is optional for device-filter preference persistence.
  }
};

const readJsonSessionValue = <T>(sessionKey: string): T | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(sessionKey);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

export const hasExplicitMultiSelectionParams = (searchParams: URLSearchParams) =>
  searchParams.has("customerId") || searchParams.has("group") || searchParams.has("storeIds");

export const getMultiSelectionFromSearchParams = (
  searchParams: URLSearchParams,
): StoreInsightsMultiSelectionState => ({
  customerIds: parseIntegerArray(searchParams.get("customerId")),
  groups: searchParams.has("group")
    ? (searchParams.get("group") ?? "").split(",").map((group) => group.trim())
    : [],
  storeIds: parseIntegerArray(searchParams.get("storeIds")),
});

export const readMultiSelectionFromSession = (
  sessionKey: string,
): StoreInsightsMultiSelectionState | null => {
  const parsed = readJsonSessionValue<{
    customerId?: number | null;
    customerIds?: unknown;
    group?: string | null;
    groups?: unknown;
    storeIds?: unknown;
    deviceType?: unknown;
  }>(sessionKey);

  if (!parsed) {
    return null;
  }

  return {
    customerIds: Array.isArray(parsed.customerIds)
      ? parsed.customerIds.filter(
          (id): id is number => typeof id === "number" && Number.isFinite(id),
        )
      : typeof parsed.customerId === "number" && Number.isFinite(parsed.customerId)
        ? [parsed.customerId]
        : [],
    groups: Array.isArray(parsed.groups)
      ? parsed.groups.filter((group): group is string => typeof group === "string")
      : typeof parsed.group === "string"
        ? parsed.group
            .split(",")
            .map((group) => group.trim())
            .filter(Boolean)
        : [],
    storeIds: Array.isArray(parsed.storeIds)
      ? parsed.storeIds.filter((id): id is number => typeof id === "number" && Number.isFinite(id))
      : [],
    deviceType: parseOptionalDeviceType(parsed.deviceType),
  };
};

export const writeMultiSelectionToSession = (
  sessionKey: string,
  selection: StoreInsightsMultiSelectionState,
) => {
  if (typeof window === "undefined") {
    return;
  }

  const current = readMultiSelectionFromSession(sessionKey);
  const nextSelection = {
    ...selection,
    ...(selection.deviceType === undefined && current?.deviceType !== undefined
      ? { deviceType: current.deviceType }
      : {}),
  };
  const serialized = JSON.stringify(nextSelection);
  if (window.sessionStorage.getItem(sessionKey) !== serialized) {
    window.sessionStorage.setItem(sessionKey, serialized);
  }
};

export const normalizeMultiSelection = (
  selection: StoreInsightsMultiSelectionState,
  visibleStores: Store[],
  customers: StoreCustomer[],
  includeUngroupedGroups: boolean,
): StoreInsightsMultiSelectionState => {
  const knownCustomerIds = new Set(
    customers.length
      ? customers.map((customer) => customer.id)
      : visibleStores.map((store) => store.customerId),
  );
  const validCustomerIds = selection.customerIds.filter((customerId) =>
    knownCustomerIds.has(customerId),
  );

  if (selection.storeIds.length > 0) {
    const requestedStoreIds = new Set(selection.storeIds);
    const matchedStores = visibleStores.filter((store) => requestedStoreIds.has(store.id));

    if (matchedStores.length > 0) {
      const availableGroups = new Set(
        matchedStores
          .map((store) => ((store as any).group as string | undefined) ?? "")
          .filter((group) => includeUngroupedGroups || group !== ""),
      );

      return {
        customerIds:
          validCustomerIds.length &&
          matchedStores.every((store) => validCustomerIds.includes(store.customerId))
            ? validCustomerIds
            : [],
        groups: selection.groups.filter((group) => availableGroups.has(group)),
        storeIds: matchedStores.map((store) => store.id),
      };
    }
  }

  const customerIdSet = new Set(validCustomerIds);
  const customerStores = validCustomerIds.length
    ? visibleStores.filter((store) => customerIdSet.has(store.customerId))
    : visibleStores;
  const availableGroups = new Set(
    customerStores
      .map((store) => ((store as any).group as string | undefined) ?? "")
      .filter((group) => includeUngroupedGroups || group !== ""),
  );
  const validGroups = selection.groups.filter((group) => availableGroups.has(group));
  const selectedStores = validGroups.length
    ? customerStores.filter((store) =>
        validGroups.includes(((store as any).group as string | undefined) ?? ""),
      )
    : customerStores;

  return {
    customerIds: validCustomerIds,
    groups: validGroups,
    storeIds: selectedStores.map((store) => store.id),
  };
};

export const getMultiSelectionUrlUpdates = (selection: StoreInsightsMultiSelectionState) => ({
  customerId: selection.customerIds.length ? selection.customerIds.join(",") : null,
  group: selection.groups.length ? selection.groups.join(",") : null,
  storeIds: selection.storeIds.length ? selection.storeIds.join(",") : null,
});

export const hasExplicitSingleStoreParams = (searchParams: URLSearchParams) =>
  searchParams.has("storeId") || searchParams.has("storeIds");

export const getSingleStoreSelectionFromSearchParams = (
  searchParams: URLSearchParams,
  visibleStores?: Store[],
): StoreInsightsSingleSelectionState => ({
  storeId: (() => {
    const explicitStoreId = parseOptionalInteger(searchParams.get("storeId"));
    if (explicitStoreId !== null) {
      return explicitStoreId;
    }

    const candidateStoreIds = parseIntegerArray(searchParams.get("storeIds"));
    if (!candidateStoreIds.length) {
      return null;
    }

    if (!visibleStores?.length) {
      return candidateStoreIds[0] ?? null;
    }

    const visibleStoreIdSet = new Set(visibleStores.map((store) => store.id));
    return candidateStoreIds.find((storeId) => visibleStoreIdSet.has(storeId)) ?? null;
  })(),
});

export const readSingleStoreSelectionFromSession = (
  sessionKey: string,
): StoreInsightsSingleSelectionState | null => {
  const parsed = readJsonSessionValue<{
    storeId?: number | null;
    hasUserSelectedStore?: unknown;
  }>(sessionKey);
  const deviceType = parseOptionalDeviceType(
    readJsonSessionValue<{ deviceType?: unknown }>(sessionKey)?.deviceType,
  );

  if (!parsed) {
    return null;
  }

  return {
    storeId:
      typeof parsed.storeId === "number" && Number.isFinite(parsed.storeId) ? parsed.storeId : null,
    deviceType,
    // Existing persisted selections predate the origin field, so retain them
    // as user intent instead of unexpectedly replacing them with multi scope.
    hasUserSelectedStore:
      typeof parsed.hasUserSelectedStore === "boolean"
        ? parsed.hasUserSelectedStore
        : parsed.storeId != null,
  };
};

export const writeSingleStoreSelectionToSession = (
  sessionKey: string,
  selection: StoreInsightsSingleSelectionState,
) => {
  if (typeof window === "undefined") {
    return;
  }

  const current = readSingleStoreSelectionFromSession(sessionKey);
  const nextSelection = {
    ...selection,
    ...(selection.deviceType === undefined && current?.deviceType !== undefined
      ? { deviceType: current.deviceType }
      : {}),
  };
  const serialized = JSON.stringify(nextSelection);
  if (window.sessionStorage.getItem(sessionKey) !== serialized) {
    window.sessionStorage.setItem(sessionKey, serialized);
  }
};

const isDemoStore = (store: Store) => {
  const name = store.name.toLowerCase();
  const group = store.group.toLowerCase();
  return name.includes("demo") || group.includes("demo");
};

export const getPreferredSingleStoreId = (storeIds: number[], visibleStores: Store[]) => {
  if (!storeIds.length || !visibleStores.length) {
    return null;
  }

  const visibleStoreMap = new Map(visibleStores.map((store) => [store.id, store]));
  const nonDemoStoreId = storeIds.find((storeId) => {
    const store = visibleStoreMap.get(storeId);
    return store ? !isDemoStore(store) : false;
  });

  if (nonDemoStoreId != null) {
    return nonDemoStoreId;
  }

  return storeIds.find((storeId) => visibleStoreMap.has(storeId)) ?? null;
};

export const writeSharedMultiSelectionFromSingleStore = (store: Store | null) => {
  writeMultiSelectionToSession(STORE_INSIGHTS_SHARED_MULTI_SELECTION_SESSION_KEY, {
    customerIds: store ? [store.customerId] : [],
    groups: [],
    storeIds: store ? [store.id] : [],
  });
};

export const writeSharedSingleSelectionFromMultiStore = (
  storeIds: number[],
  visibleStores: Store[],
) => {
  writeSingleStoreSelectionToSession(STORE_INSIGHTS_SINGLE_STORE_SELECTION_SESSION_KEY, {
    storeId: getPreferredSingleStoreId(storeIds, visibleStores),
    hasUserSelectedStore: false,
  });
};

export const normalizeSingleStoreSelection = (
  selection: StoreInsightsSingleSelectionState,
  visibleStores: Store[],
): StoreInsightsSingleSelectionState => ({
  ...selection,
  storeId:
    selection.storeId !== null && visibleStores.some((store) => store.id === selection.storeId)
      ? selection.storeId
      : (visibleStores[0]?.id ?? null),
});

export const readSharedDateWindowFromSession = (
  sessionKey: string,
): StoreInsightsSharedDateWindowState | null => {
  const parsed = readJsonSessionValue<{
    period?: unknown;
    start?: unknown;
    end?: unknown;
  }>(sessionKey);

  if (!parsed) {
    return null;
  }

  const period = normalizeSharedDateWindowPeriod(parsed.period);
  if (!period) {
    return null;
  }

  return {
    period,
    start: typeof parsed.start === "string" ? parsed.start : null,
    end: typeof parsed.end === "string" ? parsed.end : null,
  };
};

export const writeSharedDateWindowToSession = (
  sessionKey: string,
  selection: StoreInsightsSharedDateWindowState,
) => {
  if (typeof window === "undefined") {
    return;
  }

  const serialized = JSON.stringify(selection);
  if (window.sessionStorage.getItem(sessionKey) !== serialized) {
    window.sessionStorage.setItem(sessionKey, serialized);
  }
};

export const readTrendComparisonWindowFromSession = (
  sessionKey: string = STORE_INSIGHTS_SHARED_TREND_COMPARISON_SESSION_KEY,
): StoreInsightsTrendComparisonWindow | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return normalizeTrendComparisonWindow(window.sessionStorage.getItem(sessionKey));
  } catch {
    return null;
  }
};

export const writeTrendComparisonWindowToSession = (
  value: StoreInsightsTrendComparisonWindow,
  sessionKey: string = STORE_INSIGHTS_SHARED_TREND_COMPARISON_SESSION_KEY,
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (window.sessionStorage.getItem(sessionKey) !== value) {
      window.sessionStorage.setItem(sessionKey, value);
    }
  } catch {
    // Session storage is optional for comparison-window preference persistence.
  }
};

export const readTrendComparisonWeekdayAnchorFromSession = (
  sessionKey: string = STORE_INSIGHTS_SHARED_TREND_WEEKDAY_ANCHOR_SESSION_KEY,
): boolean | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const value = window.sessionStorage.getItem(sessionKey);
    return value === "true" ? true : value === "false" ? false : null;
  } catch {
    return null;
  }
};

export const writeTrendComparisonWeekdayAnchorToSession = (
  value: boolean,
  sessionKey: string = STORE_INSIGHTS_SHARED_TREND_WEEKDAY_ANCHOR_SESSION_KEY,
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(sessionKey, String(value));
  } catch {
    // Session storage is unavailable; retain the in-memory selection.
  }
};
